import { isGroupRow } from '../../stores/utils/group-row-guards';
import { excludeGroupNodes } from '../../stores/utils/exclude-group-nodes';
import type { TableNode } from '../../stores/table-store';

export interface GroupedCsvRecord extends TableNode {
  __groupId: string;
  __groupName: string;
  __groupOrder: number | '';
  __groupCollapsed: '1' | '';
  __groupColor: string; // PSTUDIO-3393
}

/**
 * Augments each non-group row with the 4 CSV grouping columns derived from
 * `settings`. Group header rows ('g') are dropped — they are reconstructed on
 * import, never emitted as CSV rows.
 *
 * Column semantics (PSTUDIO-3443):
 * - `__groupId`      member's `settings.groupId` (verbatim)
 * - `__groupName`    owning header's `settings.groupName`
 * - `__groupOrder`   1-based position within the group (honors header subRows order,
 *                    falls back to row order among peers)
 * - `__groupCollapsed` `'1'` when header `groupExpanded === false`, else `''`
 */
export const buildGroupedCsvRecords = (
  rows: TableNode[]
): GroupedCsvRecord[] => {
  // Index group headers by their id.
  const headers = new Map<string, TableNode>();
  for (const row of rows) {
    if (isGroupRow(row)) headers.set(row.id, row);
  }

  // Collect members per group to compute 1-based order.
  const membersByGroup = new Map<string, TableNode[]>();
  for (const row of rows) {
    const gid = row.settings?.groupId;
    if (!gid || isGroupRow(row)) continue;
    const list = membersByGroup.get(gid) ?? [];
    list.push(row);
    membersByGroup.set(gid, list);
  }

  // Build a per-member order map, honoring the header's subRows sequence.
  // Members not listed in subRows are ranked after listed ones, sorted by their
  // own `order` field.
  const orderByMember = new Map<string, number>();
  for (const [gid, members] of membersByGroup) {
    const subRows: string[] = headers.get(gid)?.settings?.subRows ?? [];
    const ranked = [...members].sort((a, b) => {
      const ai = subRows.indexOf(a.id);
      const bi = subRows.indexOf(b.id);
      // Listed entries keep their subRows position; unlisted entries come last
      // sorted by their own `order` value.
      const av = ai === -1 ? subRows.length + (a.order ?? 0) : ai;
      const bv = bi === -1 ? subRows.length + (b.order ?? 0) : bi;
      return av - bv;
    });
    ranked.forEach((m, i) => orderByMember.set(m.id, i + 1));
  }

  return excludeGroupNodes(rows).map((row) => {
    const gid = row.settings?.groupId ?? '';
    const header = gid ? headers.get(gid) : undefined;
    return {
      ...row,
      __groupId: gid || '',
      __groupName: header?.settings?.groupName ?? '',
      __groupOrder: gid ? (orderByMember.get(row.id) ?? '') : '',
      __groupCollapsed: header?.settings?.groupExpanded === false ? '1' : '',
      __groupColor: header?.settings?.groupColor ?? '', // PSTUDIO-3393
    } as GroupedCsvRecord;
  });
};
